import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { createMobileCrossTabHref } from "../util/mobileCrossTabLink.ts";
import { getCols } from "../util/getCols.ts";
import { DataTable } from "../components/DataTable/index.tsx";
import { DataDomainNav } from "../components/DataDomainNav.tsx";
import type { View } from "../../../../common/types.ts";
import { Trans, i18n } from "../util/useT.ts";
import type { DataTableRow } from "../components/DataTable/index.tsx";
import { PlayerFaceThumb } from "../components/PlayerFaceThumb.tsx";
import { PlayerNameLabels } from "../components/PlayerNameLabels.tsx";
import { TeamAbbrevText } from "../components/TeamAbbrevLink.tsx";
import { PositionText } from "../util/positionNames.tsx";
import { MobileTooltip } from "../components/MobileTooltip.tsx";
import { getMobileDisplayLabel, leaderDefaultStats } from "../mobile/displayPolicies.ts";
import {
  MobileActionableEmptyState,
  MobileContentHero,
  MobileEntityRow,
  MobileExpertDisclosure,
  mobileActionButtonClassNames,
} from "../mobile/mobilePrimitives.tsx";
import {
  getP3V1DefaultLeaderCategories,
  getP3V1LeaderSpotlight,
  getP3V1UserTeamHighestLeader,
  getP3V1VisibleRankingRows,
} from "../util/rankingDensity.ts";
import {
  getLeaderboardMeaningfulness,
  getPreviousSeasonReviewLabel,
  getStatAccumulatingMessage,
} from "../mobile/statMeaningfulness.ts";

type LeaderCategory = View<"leaders">["categories"][number];
type LeaderDataRow = LeaderCategory["leaders"][number];
type LeaderRow = Omit<LeaderDataRow, "firstNameShort"> & {
  firstNameShort?: string;
};

export const LeadersTopText = ({
  includeHighlight,
  noHighlightActive,
}: {
  includeHighlight?: boolean;
  noHighlightActive?: boolean;
}) => {
  return (
    <>
      <p>
        {i18n.t("leaders.eligibleOnly", {
          ns: "ui",
          defaultValue: "Only eligible players are shown",
        })}{" "}
        (<i>{i18n.t("leaders.forExample", { ns: "ui", defaultValue: "e.g." })}</i>{" "}
        {i18n.t("leaders.eligibleExampleBasketball", {
          ns: "ui",
          defaultValue:
            "a player shooting 2 for 2 on the season is not eligible for the league lead in FG%",
        })}
        ).
      </p>
      {includeHighlight ? (
        <p>
          <Trans
            i18nKey="leaders.highlightBlue"
            ns="ui"
            defaults="Players from your team are <blue>highlighted in blue</blue>."
            components={{ blue: <span className="text-info" /> }}
          />{" "}
          {noHighlightActive ? (
            ""
          ) : (
            <>
              <Trans
                i18nKey="leaders.highlightGreen"
                ns="ui"
                defaults="Active players are <green>highlighted in green</green>."
                components={{ green: <span className="text-success" /> }}
              />{" "}
            </>
          )}
          <Trans
            i18nKey="leaders.highlightRed"
            ns="ui"
            defaults="Hall of Famers are <red>highlighted in red</red>."
            components={{ red: <span className="text-danger" /> }}
          />
        </p>
      ) : null}
    </>
  );
};

const Leaders = ({
  categories,
  highlightActiveAndHOF,
  playoffs,
  season,
  statType,
  userTeamGamesPlayed,
}: View<"leaders">) => {
  const categoriesToDisplay = getP3V1DefaultLeaderCategories(categories, leaderDefaultStats);
  const noQualifiedLeaders = categoriesToDisplay.every((cat) => cat.leaders.length === 0);

  useTitleBar({
    title: i18n.t("leaders.title", { ns: "ui", defaultValue: "League Leaders" }),
    jumpTo: !window.mobile && !noQualifiedLeaders,
    jumpToSeason: season,
    dropdownView: "leaders",
    dropdownFields: {
      seasonsAndCareer: season,
      statTypesStrict: statType,
      playoffsCombined: playoffs,
    },
  });

  // When season is "all", the season is displayed inline, so columns need to be a bit wider
  const colClassName = season === "all" ? "col-12 col-md-6 col-xl-4" : "col-12 col-sm-6 col-lg-4";

  const totals = statType === "totals";

  // Pure data input for the A3 meaningfulness gate (no spotlight card renders;
  // the #1 rows already live in their category lists).
  const leaderSpotlight = getP3V1LeaderSpotlight(categoriesToDisplay);
  const userTeamHighestLeader = getP3V1UserTeamHighestLeader(categoriesToDisplay);

  // A3: "data exists but is not meaningful yet" gate (early season or zero top
  // value). Additive on top of the P2S-5 noQualifiedLeaders empty state below.
  const leadersMeaningfulness = getLeaderboardMeaningfulness({
    topValue: leaderSpotlight?.leader.stat,
    userTeamGamesPlayed,
  });
  const showLeadersAccumulating =
    !noQualifiedLeaders &&
    !leadersMeaningfulness.meaningful &&
    userTeamGamesPlayed !== undefined &&
    typeof season === "number";
  const leadersScopeValue =
    season === "career"
      ? i18n.t("leaders.mobileScopeCareer", {
          ns: "ui",
          defaultValue: "Career leaders",
        })
      : season === "all"
        ? i18n.t("leaders.mobileScopeAll", {
            ns: "ui",
            defaultValue: "All seasons",
          })
        : i18n.t("leaders.mobileScopeSeason", {
            ns: "ui",
            defaultValue: "{{season}} season leaders",
            season,
          });
  const leadersEmptyKind =
    statType === "totals" || season === "career" || season === "all" || playoffs !== "regularSeason"
      ? "no-filter-results"
      : "not-generated";
  const leadersEmptyReason =
    leadersEmptyKind === "not-generated"
      ? i18n.t("leaders.p2s05NoGeneratedReason", {
          ns: "ui",
          defaultValue:
            "League leaders need enough played games before eligible rankings exist.",
        })
      : i18n.t("leaders.p2s05NoFilterResultsReason", {
          ns: "ui",
          defaultValue:
            "No leaders match your filters yet — it could be the season, playoffs, or stat mode filter.",
        });
  const getLeaderContext = (p: LeaderRow) => {
    const numericSeason = season === "career" ? undefined : season === "all" ? p.season : season;

    // M3-1: the team_history page became a roster-hosted sheet, so the career
    // context now points at the team's roster (where that sheet lives).
    let teamUrlParts;
    if (season === "career") {
      teamUrlParts = ["roster", `${p.abbrev}_${p.tid}`];
    } else {
      teamUrlParts = ["roster", `${p.abbrev}_${p.tid}`, numericSeason];
    }

    return {
      numericSeason,
      seasonText: p.season !== undefined ? ` ${p.season}` : "",
      teamUrlParts,
    };
  };

  const renderLeaderName = (p: LeaderRow) => {
    const { numericSeason } = getLeaderContext(p);

    return (
      <PlayerNameLabels
        pid={p.pid}
        injury={p.injury}
        season={numericSeason}
        skills={p.skills}
        defaultWatch={p.watch}
        firstName={p.firstNameShort ?? p.firstName}
        firstNameShort={p.firstNameShort}
        lastName={p.lastName}
        nameOnly
      />
    );
  };

  const renderLeaderMeta = (p: LeaderRow) => {
    const { seasonText, teamUrlParts } = getLeaderContext(p);

    return (
      <>
        <a href={createMobileCrossTabHref(helpers.leagueUrl(teamUrlParts))} className="me-2">
          <TeamAbbrevText tid={p.tid} abbrev={p.abbrev} />
          {seasonText}
        </a>
        <PositionText pos={p.pos} />
      </>
    );
  };

  const getLeaderCategoryDisplay = (cat: LeaderCategory) => {
    const cols = getCols(["#", "Name", `stat:${cat.stat}`]);
    const statCol = cols[2]!;
    if (cat.titleOverride === statCol.desc) {
      throw new Error("Useless titleOverride");
    }

    const title =
      cat.titleOverride ?? statCol.desc ?? statCol.title ?? getMobileDisplayLabel(cat.stat);

    return {
      cols,
      desc: cat.titleOverride ? statCol.desc : undefined,
      // D2 full-data finding: with the zh locale the translated category title
      // often equals the stat column title ("助攻 (助攻)"), so only append the
      // parenthetical stat label when it adds information.
      heading: title === statCol.title ? title : `${title} (${statCol.title})`,
      statCol,
      title,
    };
  };

  return (
    <div
    >
      {/* G2e: League Leaders is the single converged Data entry; the data-center
          hub tab strip retired in favor of this phase-neutral domain nav row. */}
      <DataDomainNav activeView="leaders" />
      {noQualifiedLeaders ? (
        <>
          <MobileActionableEmptyState
            action={
              <a
                className={mobileActionButtonClassNames.primary}
                href={helpers.leagueUrl(["daily_schedule"])}
              >
                {i18n.t("leaders.p2s05GameDayCta", {
                  ns: "ui",
                  defaultValue: "Go to matchday to generate data",
                })}
              </a>
            }
            impact={i18n.t("leaders.mobileEmptyImpact", {
              ns: "ui",
              defaultValue: "Eligible rankings fill in here once enough games are played.",
            })}
            className="mb-3"
            data-leaders-no-data-reason=""
            reason={leadersEmptyKind === "not-generated" ? "not-started" : "empty"}
            reasonText={<span>{leadersEmptyReason}</span>}
            title={i18n.t("leaders.mobileNoDataTitle", {
              ns: "ui",
              defaultValue: "No eligible leaders yet",
            })}
          />
        </>
      ) : showLeadersAccumulating && typeof season === "number" ? (
        <p
          className="text-body-secondary"
          data-a3-stat-accumulating=""
          data-a3-stat-accumulating-reason={
            leadersMeaningfulness.meaningful ? undefined : leadersMeaningfulness.reason
          }
        >
          {getStatAccumulatingMessage(userTeamGamesPlayed ?? 0)}
          {" · "}
          <a data-a3-previous-season-review="" href={helpers.leagueUrl(["leaders", season - 1])}>
            {getPreviousSeasonReviewLabel(season - 1)}
          </a>
        </p>
      ) : (
        <>
          {/* B2a content-first migration (v5Content contract): the first
              screen IS the top-3 leaderboard rows per popular category. The
              only narration is the single scope explainer line; eligibility
              notes move into the expert disclosure with the full tables. */}
          <MobileContentHero
            data-user-team-highest-rank={userTeamHighestLeader?.rank ?? "none"}
            data-v5-leaders-top-list-first-screen=""
            explainer={leadersScopeValue}
          >
            <div className="row" data-popular-leader-entry-cards="" style={{ marginTop: -14 }}>
              {categoriesToDisplay.map((cat) => {
                const { desc, heading } = getLeaderCategoryDisplay(cat);
                const visibleLeaders = getP3V1VisibleRankingRows<LeaderRow>(cat.leaders, 3);

                return (
                  <article
                    key={cat.stat}
                    className={colClassName}
                    data-compact-top-rows=""
                    style={{ marginTop: 14 }}
                  >
                    <MobileTooltip title={desc}>
                      <h3>{heading}</h3>
                    </MobileTooltip>
                    {visibleLeaders.map((p, index) => (
                      <MobileEntityRow
                        key={p.key}
                        data-top-three-emphasis={index < 3 ? "" : undefined}
                        highlighted={p.userTeam}
                        media={
                          // C2: face thumbnail (lazy + cached); the server
                          // only ships face configs for these top-3 rows.
                          <PlayerFaceThumb face={p.face} imgURL={p.imgURL} pid={p.pid} size={32} />
                        }
                        rank={`#${index + 1}`}
                        title={renderLeaderName(p)}
                        meta={renderLeaderMeta(p)}
                        trailing={helpers.roundStat(p.stat, cat.stat, totals)}
                      />
                    ))}
                  </article>
                );
              })}
            </div>
          </MobileContentHero>

          <MobileExpertDisclosure
            className="mt-3"
            expertHostId="leaders-full-categories"
            count={i18n.t("leaders.mobileAdvancedCount", {
              ns: "ui",
              defaultValue: "{{count}} categories",
              count: categoriesToDisplay.length,
            })}
            data-advanced-stat-selector=""
            summary={i18n.t("leaders.mobileAdvancedSummary", {
              ns: "ui",
              defaultValue: "Full leader tables",
            })}
          >
            <LeadersTopText includeHighlight={highlightActiveAndHOF} />
            <div className="row" style={{ marginTop: -14 }}>
              {categoriesToDisplay.map((cat) => {
                const { cols, desc, heading } = getLeaderCategoryDisplay(cat);

                const rows: DataTableRow[] = cat.leaders.map((p, j) => {
                  const { numericSeason } = getLeaderContext(p);

                  return {
                    key: p.key,
                    metadata: {
                      type: "player",
                      pid: p.pid,
                      season: numericSeason ?? "career",
                      playoffs,
                    },
                    data: [
                      {
                        value: j + 1,
                        style: {
                          // Need this here rather than in cols becuase we're using hideHeader
                          width: 1,
                        },
                      },
                      <>
                        {renderLeaderName(p)}
                        <span className="mx-2">{renderLeaderMeta(p)}</span>
                      </>,
                      {
                        value: helpers.roundStat(p.stat, cat.stat, totals),
                        classNames: "text-end",
                      },
                    ],
                    classNames: {
                      "table-danger": highlightActiveAndHOF && p.hof,
                      "table-success": highlightActiveAndHOF && p.retiredYear === Infinity,
                      "table-info": p.userTeam,
                    },
                  };
                });

                return (
                  <div key={cat.stat} className={colClassName} style={{ marginTop: 14 }}>
                    <DataTable
                      cols={cols}
                      defaultSort={"disableSort"}
                      hideHeader
                      hideAllControls
                      mobileP3Export={{
                        label: `${i18n.t("leaders.title", {
                          ns: "ui",
                          defaultValue: "League Leaders",
                        })} ${cat.stat}`,
                        season: typeof season === "number" ? season : undefined,
                      }}
                      name={`LeagueLeaders_${cat.stat}`}
                      pagination
                      rows={rows}
                      title={
                        <MobileTooltip title={desc}>
                          <h3>{heading}</h3>
                        </MobileTooltip>
                      }
                    />
                  </div>
                );
              })}
            </div>
          </MobileExpertDisclosure>
        </>
      )}
    </div>
  );
};

export default Leaders;
